import { LocalDate, LocalDateTime, LocalTime, localDateTimeToInstantZoned } from '@rad/utility/datetime';
import { parseLocalDateTimeN } from '@rad/utility/parsers';
import { formatVanityName } from '../../../../common/tour-link';
export const STEPS = ['Name', 'Details', 'Homes', 'Order', 'Theme', 'Publish'];
export const NAME_STEP = 0;
export const DETAILS_STEP = 1;
export const HOMES_STEP = 2;
export const ORDER_STEP = 3;
export const THEME_STEP = 4;
export const PUBLISH_STEP = 5;
// More than this and the add button goes away; nothing is said, the list just stops growing.
export const MAX_ALERTS = 10;
export const ALERT_MAX_LENGTH = 180;
// A tour saved without a closing time closes at the last minute of its end date.
const END_OF_DAY = LocalTime.of(23, 59);
const splitInstant = (instant) => {
    const local = parseLocalDateTimeN(instant);
    return local === null
        ? { date: '', time: '' }
        : { date: local.toLocalDate().toString(), time: local.toLocalTime().toString().slice(0, 5) };
};
const stopToHome = (stop) => ({
    propertyId: stop.propertyId,
    parcelViewId: stop.parcelViewId,
    title: stop.title,
    address: stop.address,
    photo: stop.photo,
    status: stop.status,
    lat: stop.lat,
    lng: stop.lng,
});
const faqToWizard = (faq, i) => ({ key: i + 1, question: faq.question, answer: faq.answer });
const alertToWizard = (alert, i) => {
    var _a, _b;
    return ({
        key: i + 1,
        id: alert.id,
        message: alert.message,
        email: alert.email,
        text: alert.text,
        sendAt: (_b = (_a = parseLocalDateTimeN(alert.sendAt)) === null || _a === void 0 ? void 0 : _a.toString().slice(0, 16)) !== null && _b !== void 0 ? _b : '',
        status: alert.status,
        delivered: alert.delivered,
    });
};
// A tour not yet created: everything blank but the theme it starts in.
export const blankState = (theme) => ({
    title: '',
    description: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    ticketsUrl: '',
    ticketsOnSale: '',
    ticketsNote: '',
    faqs: [],
    alerts: [],
    homes: [],
    theme,
    vanityName: '',
});
export const fromTour = (tour, theme) => {
    var _a, _b, _c, _d, _e;
    const starts = splitInstant(tour.starts);
    const ends = splitInstant(tour.ends);
    return {
        title: tour.title,
        description: tour.description,
        startDate: starts.date,
        startTime: starts.time,
        endDate: ends.date,
        // Shown blank, since that is what saves it.
        endTime: ends.time === END_OF_DAY.toString() ? '' : ends.time,
        ticketsUrl: (_a = tour.ticketsUrl) !== null && _a !== void 0 ? _a : '',
        ticketsOnSale: (_c = (_b = parseLocalDateTimeN(tour.ticketsOnSale)) === null || _b === void 0 ? void 0 : _b.toString().slice(0, 16)) !== null && _c !== void 0 ? _c : '',
        ticketsNote: (_d = tour.ticketsNote) !== null && _d !== void 0 ? _d : '',
        faqs: tour.faqs.map(faqToWizard),
        alerts: tour.alerts.map(alertToWizard),
        homes: tour.stops.map(stopToHome),
        theme,
        // A tour without a name is offered its title's, unless it is archived and so may not hold one.
        vanityName: (_e = tour.vanityName) !== null && _e !== void 0 ? _e : (tour.status === 'archived' ? '' : formatVanityName(tour.title)),
    };
};
// A new title, and the custom link with it while the link is still the old title's suggestion. A live
// tour's link may already be shared, so it never follows; changing it is left to someone doing so on
// purpose.
export const followTitle = (state, title, savedStatus) => (Object.assign(Object.assign(Object.assign({}, state), { title }), (savedStatus !== 'active' && state.vanityName === formatVanityName(state.title) ? { vanityName: formatVanityName(title) } : {})));
const toInstant = (date, time) => localDateTimeToInstantZoned(LocalDate.parse(date).atTime(time));
export const startsOf = (state) => state.startDate === '' || state.startTime === ''
    ? null
    : toInstant(state.startDate, LocalTime.parse(state.startTime));
export const endsOf = (state) => state.endDate === ''
    ? null
    : toInstant(state.endDate, state.endTime === '' ? END_OF_DAY : LocalTime.parse(state.endTime));
export const ticketsOnSaleOf = (state) => state.ticketsOnSale === '' ? null : localDateTimeToInstantZoned(LocalDateTime.parse(state.ticketsOnSale));
// A pasted "eventbrite.com/e/123" is a link the admin meant, and the page only follows http(s), so
// give it the scheme it is missing rather than dropping it at render with nothing said.
export const ticketsUrlOf = (state) => {
    const url = state.ticketsUrl.trim();
    return url === '' ? null : /^https?:\/\//i.test(url) ? url : `https://${url}`;
};
export const alertComplete = (alert) => alert.message.trim() !== ''
    && alert.message.length <= ALERT_MAX_LENGTH
    && (alert.email || alert.text)
    && alert.sendAt !== '';
// Save & exit can send an alert not yet finished; the server's refusal then names the field to fix.
export const alertsOf = (state) => state.alerts.map(alert => ({
    id: alert.id,
    message: alert.message,
    sendAt: alert.sendAt === '' ? null : localDateTimeToInstantZoned(LocalDateTime.parse(alert.sendAt)).toString(),
    email: alert.email,
    text: alert.text,
}));
// Where a new alert's send time starts: the opening morning, while that is still to come. Once it has
// passed, the time starts blank for the admin to pick, since the server refuses a new one that has passed.
export const newAlertSendAt = (state, now) => {
    const morning = state.startDate === '' ? null : LocalDate.parse(state.startDate).atTime(LocalTime.of(9, 0));
    return morning !== null && morning.isAfter(now) ? morning.toString().slice(0, 16) : '';
};
// The reminders a tour usually wants, worded for its name and sent by email: when tickets go on sale, a
// week out and the day before, mid-morning. Only those with a time still to come are offered.
export const suggestedAlerts = (state, now) => {
    const name = state.title.trim() === '' ? 'the tour' : state.title.trim();
    const reminder = (date) => date.atTime(LocalTime.of(10, 0)).toString().slice(0, 16);
    const start = state.startDate === '' ? null : LocalDate.parse(state.startDate);
    return [
        state.ticketsOnSale === '' ? null : {
            sendAt: state.ticketsOnSale,
            message: `Tickets are now on sale for ${name}. Open the tour page to see ticket options.`,
        },
        start === null ? null : {
            sendAt: reminder(start.minusDays(7)),
            message: `${name} is 1 week away! If you still need tickets, open the tour page to find them.`,
        },
        start === null ? null : {
            sendAt: reminder(start.minusDays(1)),
            message: `Tomorrow's the day! We can't wait to see you at ${name}. Need more tickets? Open the tour page.`,
        },
    ]
        .filter((suggestion) => suggestion !== null)
        .filter(suggestion => LocalDateTime.parse(suggestion.sendAt).isAfter(now))
        .map(suggestion => (Object.assign(Object.assign({}, suggestion), { id: null, email: true, text: false, status: null, delivered: null })));
};
// The field to change when the close set comes before the opening: the end date when it is the earlier
// day, else the closing time. An open-ended tour has no close to get wrong.
export const closesBeforeOpening = (state) => {
    const starts = startsOf(state);
    const ends = endsOf(state);
    return starts === null || ends === null || ends.isAfter(starts)
        ? null
        : state.endDate < state.startDate ? 'endDate' : 'endTime';
};
const missing = (message) => ({ message, isMistake: false });
const detailsProblem = (state) => startsOf(state) === null
    ? missing('Set the opening date and time to continue')
    : closesBeforeOpening(state) !== null
        ? { message: 'The tour is set to close before it opens', isMistake: true }
        : state.faqs.some(faq => faq.question.trim() === '' || faq.answer.trim() === '')
            ? missing('Each question needs an answer')
            : !state.alerts.every(alertComplete)
                ? missing('Each alert needs a message, a channel, and a send time')
                : null;
// What stands between a step and the next one, or null once it is done. The titlebar lets a step be
// jumped to only when every step before it comes back null.
export const stepProblem = (state, step) => step === NAME_STEP
    ? (state.title.trim() === '' ? missing('Give the tour a name to continue') : null)
    : step === DETAILS_STEP
        ? detailsProblem(state)
        : step === HOMES_STEP
            ? (state.homes.length < 2 ? missing('Add at least two homes to continue') : null)
            : null;
// A save's refusal, by the field each problem names, kept as the server names it: an alert's send time
// is alerts[0].sendAt, which is how the steps look it up.
export const errorsByField = (problems) => problems.reduce((errors, { field, message }) => { var _a; return (Object.assign(Object.assign({}, errors), { [field]: [...((_a = errors[field]) !== null && _a !== void 0 ? _a : []), message] })); }, {});
const DETAILS_FIELDS = ['starts', 'ends', 'ticketsUrl', 'ticketsOnSale', 'ticketsNote', 'faqs', 'alerts'];
// Where a save failure sends the admin back to, by the field the server named. A question's field
// comes back as faqs[0].answer, so it is matched by what it starts with.
export const stepForField = (field) => DETAILS_FIELDS.some(name => field.startsWith(name))
    ? DETAILS_STEP
    : field.startsWith('stops')
        ? HOMES_STEP
        : field.startsWith('themeSettings')
            ? THEME_STEP
            : NAME_STEP;
